import styled from '@emotion/styled';
import { type HTMLAttributes } from 'react';
import { Icon } from '../icon';

type StepperProps = {
  steps: number;
  active?: number;
  onActive?: (value: number) => void;
  infinite?: boolean;
  checkedArray: boolean[];
} & HTMLAttributes<HTMLDivElement>;

const StepperWrapper = styled('div')`
  background: ${({ theme }) => theme.tokens.color.global.white.value};
  justify-content: space-between;
  display: flex;
  width: fit-content;
  position: relative;
  height: 20px;
  padding: 0 7px;
`;

const StepContainer = styled('div')`
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  :hover {
    cursor: pointer;
  }
`;

const Step = styled('div')<{ checked: boolean }>`
  width: 6px;
  height: 6px;
  display: inline-block;
  border-radius: 3px;
  z-index: 1;
  position: relative;

  background-color: ${({ checked, theme }) =>
    checked ? 'transparent' : theme.tokens.color.global.teal.teal500.value};

  margin: auto 0;
`;

const Circle = styled('div')<{ active: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;

  background-color: ${({ theme }) =>
    theme.tokens.color.global.teal.teal300.value};

  transform: translate(${({ active }) => active * 20 + 7}px);
  transition-duration: 0.5s;
`;

const CustomCheck = styled(Icon)`
  position: absolute;
  top: -120%;
  left: -120%;
  transform: scale(0.5);

  path {
    fill: ${({ theme }) => theme.tokens.color.global.teal.teal500.value};
    stroke: ${({ theme }) => theme.tokens.color.global.teal.teal500.value};
  }
`;

export function Stepper({
  steps,
  infinite = false,
  active = 0,
  onActive,
  checkedArray,
  ...rest
}: StepperProps): JSX.Element {
  const handleClick = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    onActive?.(index);
  };

  return (
    <StepperWrapper {...rest}>
      {[...Array(steps)].map((_, index) => (
        <StepContainer
          key={index}
          onClick={(event) => handleClick(event, index)}
        >
          <Step checked={checkedArray[index]}>
            {checkedArray[index] && <CustomCheck icon="CheckIcon" />}
          </Step>
        </StepContainer>
      ))}
      <Circle active={active} />
    </StepperWrapper>
  );
}

export type { StepperProps };
